import Assign from 'core-js-pure/features/object/assign';
import {
  KustomerWindow,
  Kustomer,
  KustomerWidgetEventTypes,
} from 'widget/types';
import { setupIframeForApi, getIframeForWidgetMain, applyNonceToDynamicScripts } from 'helpers/browser';
import { runQueue } from 'helpers/queue';
import { DescribeCustomerParameters } from 'core_main/sdk/describe_customer/types';
import { DescribeConversationParameters } from 'core_main/sdk/describe_conversation/types';
import { LoginParameters } from 'core_main/sdk/login/types';
import { IsLoggedInParameters } from 'core_main/sdk/is_logged_in/types';
import { HandoffConversationParameters } from 'core_main/sdk/handoff/types';
import { installPolyfills } from 'polyfills/helpers';
import { getNonce } from 'helpers/nonce/helpers';
import { kustomerWidgetEventTypes } from '../widget_main/globals/constants';
import {
  CreateConversationEventDetail,
  CreateConversationWidgetApiParameters,
  StartParameters,
  OpenConversationByIdWidgetApiParameters,
  CreateConversationInDraftWidgetApiParameters,
} from './types';
import cssReset from './cssReset';
import {
  makeIncompleteStartError,
  parseStartParameters,
  setKustomerStyleSheetOnParent,
  getWidgetIframeAndWindow,
} from './helpers';
import { DEFAULT_Z_INDEX, publicConstants } from './constants';
import { buildSrcdoc } from './build_srcdoc';

const kustomerWindow: KustomerWindow = window;
const kustomer = kustomerWindow.Kustomer as Kustomer;

const setupWindow = () => {
  kustomerWindow.Kustomer = Assign(kustomer, {
    constants: publicConstants,
    startFinished: false,
    startParameters: {},
    startCallback: null,
    listeners: {},
    addListener(eventType: KustomerWidgetEventTypes, cb) {
      if (!kustomerWidgetEventTypes[eventType]) {
        return console.error(`${eventType} is not a valid event name`);
      }
      if (typeof cb !== 'function') {
        return undefined;
      }

      this.listeners[eventType] = this.listeners[eventType] || [];
      this.listeners[eventType].push(cb);
      return undefined;
    },
    removeListener(eventType: KustomerWidgetEventTypes, cb) {
      if (!this.listeners[eventType] || typeof cb !== 'function') {
        return;
      }
      this.listeners[eventType] = this.listeners[eventType].filter(
        (callback) => callback !== cb,
      );
    },
    stop(callback) {
      const iframe = getIframeForWidgetMain();
      iframe?.remove();
      this.startFinished = false;
      if (typeof callback === 'function') {
        callback();
      }
    },
    start(parameters: StartParameters, cb) {
      this.startParameters = parseStartParameters(parameters);
      this.startCallback = cb;
      if (getIframeForWidgetMain()) {
        return;
      }
      setKustomerStyleSheetOnParent();

      const zIndex = this.startParameters.zIndex || DEFAULT_Z_INDEX;
      const position = this.startParameters.chatIconPosition;
      const horizontalPadding =
        position.alignment === 'right'
          ? `right: ${Number(position.horizontalPadding)}px`
          : `left: ${Number(position.horizontalPadding)}px`;

      // Get nonce from script element, similar to widget_queue
      const nonce = this.getNonce();

      setupIframeForApi(
        'kustomer-ui-sdk-iframe',
        `display: none; border: 0; width: 0px; height: 0px; position: fixed; bottom: ${Number(
          position.verticalPadding,
        )}px; ${horizontalPadding}; z-index: ${Number(zIndex)};`,
        buildSrcdoc({
          cssReset,
          chatIconAlign: position.alignment,
          coreSrc: `${process.env.CHAT_WEB_BASE_URL}/core.js`,
          widgetSrc: process.env.KUSTOMER_WIDGET_MAIN_INCLUDE!,
          apiKey: kustomer.a,
          shop: kustomer.b,
          nonce,
        }),
        'Kustomer Widget Iframe',
        nonce,
      );
    },
    open(callback) {
      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerOpen', {
          detail: { callback },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
    close(callback) {
      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerClose', {
          detail: { callback },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
    login(parameters: LoginParameters, callback) {
      const iframe = getIframeForWidgetMain();

      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerLogin', {
          detail: { parameters, callback },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
    isLoggedIn(parameters: IsLoggedInParameters, callback) {
      const { iframe, contentWindow } = getWidgetIframeAndWindow();
      if (iframe && contentWindow) {
        contentWindow.KustomerCore?.isLoggedIn(parameters, callback);
      }
    },
    logout(cb) {
      const { iframe, contentWindow } = getWidgetIframeAndWindow();
      if (iframe && contentWindow) {
        contentWindow.KustomerCore?.logout(cb);
      }
    },
    handoff(parameters: HandoffConversationParameters, callback) {
      const { iframe, contentWindow } = getWidgetIframeAndWindow();
      if (iframe && contentWindow) {
        contentWindow.KustomerCore?.handoff(parameters, callback);
      }
    },
    describeCustomer(parameters: DescribeCustomerParameters, cb) {
      const { iframe, contentWindow } = getWidgetIframeAndWindow();
      if (iframe && contentWindow) {
        contentWindow.KustomerCore?.describeCustomer(parameters, cb);
      }
    },
    describeConversation(parameters: DescribeConversationParameters, cb) {
      const { iframe, contentWindow } = getWidgetIframeAndWindow();
      if (iframe && contentWindow) {
        contentWindow.KustomerCore?.describeConversation(parameters, cb);
      }
    },
    isChatAvailable() {
      const { iframe, contentWindow } = getWidgetIframeAndWindow();
      if (iframe && contentWindow) {
        return contentWindow.KustomerCore?.isChatAvailable();
      }

      return undefined;
    },
    getUnreadCount() {
      const { iframe, contentWindow } = getWidgetIframeAndWindow();
      if (iframe && contentWindow) {
        return contentWindow.KustomerCore?.getUnreadCount();
      }

      return undefined;
    },
    createConversation(
      parameters: CreateConversationWidgetApiParameters,
      callback,
    ) {
      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const message = parameters?.message;
        const payload = parameters?.payload;
        const assistantId = parameters?.assistantId;
        const dialogId = parameters?.dialogId;
        const custom = parameters?.customAttributes;

        const detail: CreateConversationEventDetail = {
          callback,
          message,
          payload,
          assistantId,
          dialogId,
          custom,
        };

        const event = new CustomEvent('kustomerCreateConversation', {
          detail,
        });

        iframe.contentWindow.dispatchEvent(event);
      }
    },
    createConversationInDraft(
      parameters: CreateConversationInDraftWidgetApiParameters,
      callback?,
    ) {
      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerOpenDraftConversation', {
          detail: { message: parameters?.message, callback },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
    openConversationById(
      parameters: OpenConversationByIdWidgetApiParameters,
      callback?,
    ) {
      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerOpenConversationById', {
          detail: { conversationId: parameters?.conversationId, callback },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
    getOpenConversations(callback) {
      if (!this.startFinished) {
        const errorMessage = makeIncompleteStartError('getOpenConversations');
        console.error(errorMessage);
        if (typeof callback === 'function') {
          callback(null, new Error(errorMessage));
        }
        return;
      }

      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerGetOpenConversations', {
          detail: { callback },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
    openKbArticle(articleId: string, cb?) {
      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerOpenKbArticle', {
          detail: { articleId, cb },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
    openKbCategory(categoryId: string, cb?) {
      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerOpenKbCategory', {
          detail: { categoryId, cb },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
    getNonce(scriptEmbedElement?: HTMLScriptElement | null) {
      return getNonce(scriptEmbedElement);
    },
  });

  if (kustomer) {
    runQueue(kustomer);
  }
};

installPolyfills().then(() => {
  // Apply CSP nonce to dynamically loaded scripts
  /*
    Simplified nonce detection logic:
    - We now only look for a script tag with [data-kustomer-api-key],
      as all entry points (widget.js, core.js, widget_api) now include this attribute.
    The getNonce helper returns the nonce if present.
  */
  const scriptEmbedElement = document.querySelector('[data-kustomer-api-key]') as HTMLScriptElement | null;
  const nonce = getNonce(scriptEmbedElement);
  if (nonce) {
    // Set up global interception to ensure all dynamically loaded scripts (this only includes resources from kustomer domains)
    // receive the correct CSP nonce. This is required for compliance with strict CSP policies.
    // Must be called before any dynamic imports or script loads occur.
    applyNonceToDynamicScripts(nonce);
  }

  setupWindow();
});
